import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { Snackbar } from '@paysense-india-services/elevate-web'

import { ROUTES } from '@/constants/routes'

import { useProductConfig } from '@/contexts/ProductConfigContext'

import useFetch from '@/hooks/useFetch'
import useOtpPrerequisiteGuard from '@/hooks/useOtpPrerequisiteGuard'

import { OTP_PREREQUISITE_KIND } from '@/utils/otpPrerequisites'
import { decodeEmailFromQuery } from '@/utils/urlParams'

import AuthFormShell from '@/components/layouts/AuthFormShell'
import OtpEntry from '@/components/OtpEntry'

import { PRODUCT_OPERATIONS } from '@/config/productApi'
import { PRODUCT_SOURCES } from '@/config/products'
import {
  getStoredMobile,
  getStoredOtpId,
  setStoredOtpId,
  setStoredPendingEmailOtp
} from '@/config/sourceStorage'

import {
  DEFAULT_CAN_RESEND_AFTER,
  DEFAULT_EXPIRES_IN,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES
} from './constants'

const OTPVerificationPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { appendProductParams, config } = useProductConfig()
  const email = decodeEmailFromQuery(searchParams.get('email'))

  const expiresIn = Number.parseInt(
    searchParams.get('expires_in') || String(DEFAULT_EXPIRES_IN),
    10
  )
  const canResendAfter = Number.parseInt(
    searchParams.get('can_resend_after') || String(DEFAULT_CAN_RESEND_AFTER),
    10
  )

  const [resendCount, setResendCount] = useState(0)

  const { loading: verifyLoading, apiRequest: callVerify } = useFetch()
  const { loading: resendLoading, apiRequest: callResend } = useFetch()

  useOtpPrerequisiteGuard(OTP_PREREQUISITE_KIND.EMAIL, { email })

  useEffect(() => {
    if (!email) {
      navigate(`${ROUTES.EMAIL_VERIFICATION}?${appendProductParams().toString()}`)
    }
  }, [email, navigate, appendProductParams])

  const handleVerify = (otp, setOtpError) => {
    if (!email) {
      setOtpError(ERROR_MESSAGES.MISSING_EMAIL)
      Snackbar.show(ERROR_MESSAGES.MISSING_EMAIL, { type: 'error' })
      navigate(`${ROUTES.EMAIL_VERIFICATION}?${appendProductParams().toString()}`)
      return
    }

    setOtpError('')
    callVerify(
      PRODUCT_OPERATIONS.VERIFY_EMAIL_OTP,
      {
        email,
        otp,
        mobile: getStoredMobile(),
        otpId: getStoredOtpId()
      },
      {
        onSuccess: result => {
          const isBusinessFailure = config.source !== PRODUCT_SOURCES.LP && !result?.ok
          if (isBusinessFailure) {
            const msg = result?.message ?? ERROR_MESSAGES.INCORRECT_OTP
            setOtpError(msg)
            Snackbar.show(msg, { type: 'error' })
            return
          }

          Snackbar.show(result.message ?? SUCCESS_MESSAGES.EMAIL_VERIFIED, { type: 'success' })
          setStoredPendingEmailOtp(null)
          setStoredOtpId(null)

          const queryParams = appendProductParams({
            status: 'success',
            email: result.email || email
          })
          if (result.verifiedAt) queryParams.set('verified_at', result.verifiedAt)
          if (result.verificationStatus)
            queryParams.set('verification_status', result.verificationStatus)

          navigate(`${ROUTES.VERIFICATION_STATUS}?${queryParams.toString()}`)
        },
        onError: err => {
          const errorMessage = err?.parsedMessage || ERROR_MESSAGES.INCORRECT_OTP
          setOtpError(errorMessage)
          Snackbar.show(errorMessage, { type: 'error' })
        }
      }
    )
  }

  const handleResendOTP = () => {
    if (!email) {
      Snackbar.show(ERROR_MESSAGES.MISSING_EMAIL, { type: 'error' })
      navigate(`${ROUTES.EMAIL_VERIFICATION}?${appendProductParams().toString()}`)
      return
    }

    callResend(
      PRODUCT_OPERATIONS.RESEND_EMAIL_OTP,
      { email, mobile: getStoredMobile(), otpId: getStoredOtpId() },
      {
        onSuccess: result => {
          if (!result?.ok) {
            Snackbar.show(ERROR_MESSAGES.RESEND_FAILED, { type: 'error' })
            return
          }

          if (result.otpId) setStoredOtpId(result.otpId)

          const newCanResendAfter = result.canResendAfterSeconds ?? canResendAfter
          const newExpiresIn = result.expiresInSeconds ?? expiresIn

          setResendCount(c => c + 1)

          const queryParams = appendProductParams({
            email,
            expires_in: String(newExpiresIn),
            can_resend_after: String(newCanResendAfter)
          })
          navigate(`${ROUTES.OTP_VERIFICATION}?${queryParams.toString()}`, { replace: true })

          Snackbar.show(result.message ?? SUCCESS_MESSAGES.OTP_RESENT, { type: 'success' })
        },
        onError: err => {
          const errorMessage = err?.parsedMessage || ERROR_MESSAGES.RESEND_FAILED
          Snackbar.show(errorMessage, { type: 'error' })
        }
      }
    )
  }

  return (
    <AuthFormShell headerVariant="back">
      <OtpEntry
        incompleteOtpMessage={ERROR_MESSAGES.INCOMPLETE_OTP}
        isResendLoading={resendLoading}
        key={resendCount}
        onResend={handleResendOTP}
        onVerify={handleVerify}
        resendAfterSeconds={canResendAfter}
        subtitle={`We just sent an OTP to your email ${email || 'your registered email'}`}
        title="Enter OTP"
        verifyButtonText="Verify email"
        verifyLoading={verifyLoading}
      />
    </AuthFormShell>
  )
}

export default OTPVerificationPage
